import { Button, Grid } from '@mui/material';
import { Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AccountData, Language, StorefrontFAQData } from '../../../api';
import TitledDrawer from '../../../common/ui/TitledDrawer';
import { FormTextField } from '../../../experienceSettings/formComponents/FormTextField';
import { useAuth } from '../../../session/InternalAuthProvider';

type FAQEditDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: (faq: StorefrontFAQData) => void;
  faq: StorefrontFAQData | null;
};

type FAQFormValues = {
  question: Record<string, string>;
  answer: Record<string, string>;
};

export const FAQEditDialog = ({ open, onClose, onSave, faq }: FAQEditDialogProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const languages = account.customer_facing_widgets_languages;

  const initialValues: FAQFormValues = {
    question: faq?.question || {},
    answer: faq?.answer || {},
  };

  const handleSubmit = (values: FAQFormValues) => {
    onSave(values);
    onClose();
  };

  const isEdit = faq !== null;

  return (
    <TitledDrawer title={isEdit ? t('storefronts.edit_faq') : t('storefronts.add_faq')} open={open} onClose={onClose}>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
        {({ submitForm }) => (
          <>
            <Grid
              container
              flexDirection="column"
              flexWrap="nowrap"
              gap={1.5}
              flex={1}
              minHeight={0}
              sx={{
                overflowY: 'auto',
                pt: { xs: '12px', md: '18px' },
                pb: 2,
                paddingInline: { xs: '12px', md: '24px' },
              }}>
              {languages.map((lang) => (
                <FormTextField
                  key={`q_${lang}`}
                  fieldName={`question.${lang}`}
                  label={t('storefronts.question_lang', { lang: t(`emailTemplates.${lang}`) })}
                  dir={lang === Language.HE ? 'rtl' : 'ltr'}
                  width="100%"
                  isFieldUpdate
                />
              ))}
              {languages.map((lang) => (
                <FormTextField
                  key={`a_${lang}`}
                  fieldName={`answer.${lang}`}
                  label={t('storefronts.answer_lang', { lang: t(`emailTemplates.${lang}`) })}
                  dir={lang === Language.HE ? 'rtl' : 'ltr'}
                  minRows={3}
                  width="100%"
                  isFieldUpdate
                />
              ))}
            </Grid>
            <Grid
              container
              gap={1}
              justifyContent="flex-end"
              sx={{
                flexShrink: 0,
                py: 1.5,
                paddingInline: { xs: '12px', md: '24px' },
                borderTop: '1px solid',
                borderColor: 'mb.lineSoft',
              }}>
              <Button variant="outlined" color="inherit" onClick={onClose}>
                {t('cancel')}
              </Button>
              <Button variant="contained" onClick={submitForm}>
                {t('save')}
              </Button>
            </Grid>
          </>
        )}
      </Formik>
    </TitledDrawer>
  );
};
